import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  useTheme,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Snackbar,
  Alert,
  Grid,
  TextField,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import '../../../node_modules/react-datepicker/dist/react-datepicker.css';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import { useAuth } from '../../AuthContext';

// Custom CSS for react-datepicker to match theme
const datePickerStyles = `
  .react-datepicker {
    font-family: 'Roboto', sans-serif;
    font-size: 0.85rem;
    border: 1px solid ${tokens('light').grey[300]};
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    overflow: hidden;
  }

  .react-datepicker__month-container {
    padding: 6px;
  }

  .react-datepicker__header {
    background-color: ${tokens('light').blueAccent[500]};
    border-bottom: none;
    padding-top: 12px;
    padding-bottom: 8px;
  }

  .react-datepicker__current-month {
    color: #ffffff;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .react-datepicker__navigation {
    top: 14px;
  }

  .react-datepicker__navigation-icon::before {
    border-color: #ffffff;
    border-width: 2px 2px 0 0;
  }

  .react-datepicker__day-names {
    margin-top: 4px;
  }

  .react-datepicker__day-name {
    color: rgba(255, 255, 255, 0.85);
    font-weight: 600;
    width: 2.2rem;
    line-height: 2rem;
    margin: 0.15rem;
  }

  .react-datepicker__day {
    color: ${tokens('light').grey[800]};
    width: 2.2rem;
    line-height: 2.2rem;
    margin: 0.15rem;
    border-radius: 50%;
    transition: background-color 0.15s ease;
  }

  .react-datepicker__day:hover {
    background-color: ${tokens('light').blueAccent[200]};
    border-radius: 50%;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: ${tokens('light').blueAccent[500]};
    color: #ffffff;
    border-radius: 50%;
    font-weight: 600;
  }

  .react-datepicker__day--today {
    font-weight: 700;
    color: ${tokens('light').blueAccent[500]};
  }

  .react-datepicker__day--disabled {
    color: ${tokens('light').grey[400]};
    cursor: not-allowed;
  }

  .react-datepicker__day--outside-month {
    color: ${tokens('light').grey[400]};
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker-popper {
    z-index: 1000;
    padding-top: 6px;
  }
`;

// Validation schema
const reportSchema = Yup.object().shape({
  imei_list: Yup.array()
    .min(1, 'At least one pump must be selected')
    .required('Pump selection is required'),
  startDate: Yup.date()
    .required('Start date is required')
    .max(new Date(), 'Start date cannot be in the future')
    .nullable(),
  endDate: Yup.date()
    .required('End date is required')
    .max(new Date(), 'End date cannot be in the future')
    .when('startDate', (startDate, schema) =>
      startDate
        ? schema
            .min(startDate, 'End date cannot be before start date')
            .test('max-one-month', 'Date range cannot exceed 1 month (31 days)', function (endDate) {
              if (!endDate || !startDate) return true;
              const diffDays = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
              return diffDays <= 31;
            })
        : schema
    )
    .nullable(),
});

const PumpReportGenerator = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { authorizedFetch } = useAuth();
  const [pumps, setPumps] = useState([]);
  const [pumpsLoading, setPumpsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [userDownloading, setUserDownloading] = useState(false);

  useEffect(() => {
    setPumpsLoading(true);
    authorizedFetch('/pump_imei?not_included=false')
      .then((r) => r.json())
      .then((list) => setPumps(Array.isArray(list) ? list : []))
      .catch(() => setPumps([]))
      .finally(() => setPumpsLoading(false));
  }, []);

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleDownloadUserDetails = async () => {
    setUserDownloading(true);
    try {
      const response = await authorizedFetch('/consumers/report');
      if (!response.ok) {
        throw new Error('Failed to download user details');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user_details.csv';
      a.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'User details downloaded!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to download user details', severity: 'error' });
    } finally {
      setUserDownloading(false);
    }
  };

  const labelFor = (imei) => {
    const pump = pumps.find((p) => p.imei === imei);
    return pump && pump.name ? `${pump.name} (${imei})` : imei;
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (!pumps.length) {
        throw new Error('No pumps available to generate report');
      }

      const startDate = values.startDate
        ? `${values.startDate.getFullYear()}-${String(values.startDate.getMonth() + 1).padStart(2, '0')}-${String(values.startDate.getDate()).padStart(2, '0')}`
        : '';
      const endDate = values.endDate
        ? `${values.endDate.getFullYear()}-${String(values.endDate.getMonth() + 1).padStart(2, '0')}-${String(values.endDate.getDate()).padStart(2, '0')}`
        : '';

      const formData = new FormData();
      formData.append('imei_list', values.imei_list.join(','));
      formData.append('startDate', startDate);
      formData.append('endDate', endDate);

      const response = await authorizedFetch('/pumps/report', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to generate report');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pump_report_${startDate}_to_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: 'Report generated successfully!', severity: 'success' });
      resetForm();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to generate report', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <style>{datePickerStyles}</style>
      <Header title="Pump Report Generator" subtitle="Generate Reports for Selected Pumps" />
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={6}>
          <Box bgcolor={colors.primary[400]} p={3} borderRadius={2} boxShadow={3}>
            <Typography variant="h5" color={colors.greenAccent[500]} mb={2}>
              Generate Report
            </Typography>
            <Formik
              initialValues={{ imei_list: [], startDate: null, endDate: null }}
              validationSchema={reportSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, setFieldValue, isSubmitting, values }) => {
                return (
                  <Form>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Pump Selection Dropdown */}
                      <FormControl fullWidth error={touched.imei_list && !!errors.imei_list}>
                        <InputLabel sx={{ color: colors.grey[300] }}>
                          Select Pumps ({values.imei_list.length} selected)
                        </InputLabel>
                        <Select
                          multiple
                          label={`Select Pumps (${values.imei_list.length} selected)`}
                          value={values.imei_list}
                          onChange={(e) => {
                            setFieldValue('imei_list', e.target.value);
                          }}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((imei) => (
                                <Chip
                                  key={imei}
                                  label={labelFor(imei)}
                                  size="small"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onDelete={() =>
                                    setFieldValue('imei_list', values.imei_list.filter((i) => i !== imei))
                                  }
                                  sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100] }}
                                />
                              ))}
                            </Box>
                          )}
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 350, backgroundColor: colors.primary[400] },
                            },
                          }}
                          sx={{
                            backgroundColor: colors.primary[400],
                            color: colors.grey[100],
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[300] },
                            '& .MuiSvgIcon-root': { color: colors.grey[300] },
                          }}
                        >
                          {pumpsLoading && (
                            <MenuItem disabled>Loading pumps…</MenuItem>
                          )}
                          {!pumpsLoading && pumps.length === 0 && (
                            <MenuItem disabled>No pumps available</MenuItem>
                          )}
                          {pumps.map((pump) => {
                            const isSelected = values.imei_list.includes(pump.imei);
                            return (
                              <MenuItem
                                key={pump.imei}
                                value={pump.imei}
                                sx={{ color: colors.grey[100] }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  size="small"
                                  sx={{
                                    color: colors.grey[400],
                                    '&.Mui-checked': { color: colors.blueAccent[400] },
                                  }}
                                />
                                <ListItemText
                                  primary={pump.name || pump.imei}
                                  secondary={pump.name ? pump.imei : null}
                                  secondaryTypographyProps={{ color: colors.grey[400] }}
                                />
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {touched.imei_list && errors.imei_list && (
                          <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                            {errors.imei_list}
                          </Typography>
                        )}
                      </FormControl>

                      {/* Date Pickers */}
                      <Box display="flex" gap={2}>
                        <FormControl fullWidth error={touched.startDate && !!errors.startDate}>
                          <Field name="startDate">
                            {({ field, form }) => (
                              <Box>
                                <DatePicker
                                  selected={field.value}
                                  onChange={(date) => form.setFieldValue('startDate', date)}
                                  dateFormat="dd MMM yyyy"
                                  placeholderText="Start Date"
                                  maxDate={new Date()}
                                  showPopperArrow={false}
                                  popperPlacement="bottom-start"
                                  customInput={
                                    <TextField
                                      variant="outlined"
                                      InputLabelProps={{ shrink: true }}
                                      label="Start Date"
                                      error={touched.startDate && !!errors.startDate}
                                      helperText={touched.startDate && errors.startDate}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: colors.primary[400],
                                          '& fieldset': { borderColor: colors.grey[700] },
                                          '&:hover fieldset': { borderColor: colors.blueAccent[300] },
                                        },
                                        '& .MuiInputBase-input': { color: colors.grey[100] },
                                        '& .MuiInputLabel-root': { color: colors.grey[300] },
                                      }}
                                    />
                                  }
                                />
                              </Box>
                            )}
                          </Field>
                        </FormControl>
                        <FormControl fullWidth error={touched.endDate && !!errors.endDate}>
                          <Field name="endDate">
                            {({ field, form }) => (
                              <Box>
                                <DatePicker
                                  selected={field.value}
                                  onChange={(date) => form.setFieldValue('endDate', date)}
                                  dateFormat="dd MMM yyyy"
                                  placeholderText="End Date"
                                  minDate={values.startDate || undefined}
                                  maxDate={new Date()}
                                  showPopperArrow={false}
                                  popperPlacement="bottom-start"
                                  customInput={
                                    <TextField
                                      variant="outlined"
                                      InputLabelProps={{ shrink: true }}
                                      label="End Date"
                                      error={touched.endDate && !!errors.endDate}
                                      helperText={touched.endDate && errors.endDate}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: colors.primary[400],
                                          '& fieldset': { borderColor: colors.grey[700] },
                                          '&:hover fieldset': { borderColor: colors.blueAccent[300] },
                                        },
                                        '& .MuiInputBase-input': { color: colors.grey[100] },
                                        '& .MuiInputLabel-root': { color: colors.grey[300] },
                                      }}
                                    />
                                  }
                                />
                              </Box>
                            )}
                          </Field>
                        </FormControl>
                      </Box>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '10px 20px',
                          '&:hover': { backgroundColor: colors.blueAccent[800] },
                        }}
                      >
                        {isSubmitting ? 'Generating Report...' : 'Generate Report'}
                      </Button>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box bgcolor={colors.primary[400]} p={3} borderRadius={2} boxShadow={3} height="100%">
            <Typography variant="h5" color={colors.greenAccent[500]} mb={2}>
              User Details
            </Typography>
            <Typography variant="body2" color={colors.grey[300]} mb={3}>
              Download a sheet of all users with their basic details (name, mobile,
              service number, farm address, registration date) and the pumps attached
              to them. No telemetry or aggregated data is included.
            </Typography>
            <Button
              variant="contained"
              onClick={handleDownloadUserDetails}
              disabled={userDownloading}
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': { backgroundColor: colors.blueAccent[800] },
              }}
            >
              {userDownloading ? 'Downloading…' : 'Download User Details (CSV)'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PumpReportGenerator;
